import { useMemo, useState } from "react";
import { simulate, stats, type Model } from "../server/budget-engine";
import { ago, api, Drawer, Empty, Loading, Pill, useLoad, useMe } from "./lib";
import { usd } from "./finance";

type Field = { path: string; label: string; unit?: "$" | "%" | "x" | "#" | "month"; step?: number; hint?: string };
const GROUPS: { title: string; fields: Field[] }[] = [
  {
    title: "Growth",
    fields: [
      { path: "drivers.organicNew", label: "New organic paying users a month", unit: "#" },
      { path: "drivers.ramp", label: "Monthly growth multiplier", unit: "x", step: 0.01, hint: "1.05 = 5% more each month" },
      { path: "drivers.installToPaid", label: "Installs that become paying", unit: "%", step: 0.5 },
      { path: "drivers.churn", label: "Monthly cancellations", unit: "%", step: 0.5 },
      { path: "drivers.annualMix", label: "Share choosing annual", unit: "%" },
      { path: "drivers.annualRenew", label: "Annual plans that renew", unit: "%" },
      { path: "drivers.paidStart", label: "Paying users at launch", unit: "#" },
    ],
  },
  {
    title: "Price",
    fields: [
      { path: "drivers.priceMonthly", label: "Monthly price", unit: "$", step: 0.01 },
      { path: "drivers.priceAnnual", label: "Annual price", unit: "$", step: 0.01 },
      { path: "drivers.storeFee", label: "App store fee", unit: "%" },
    ],
  },
  {
    title: "Influencers and partners",
    fields: [
      { path: "drivers.infCreators", label: "Creators a month", unit: "#" },
      { path: "drivers.infFee", label: "Fee per creator", unit: "$" },
      { path: "drivers.infUsersPerCreator", label: "Paying users per creator", unit: "#" },
      { path: "drivers.partnersNew", label: "New partners a month", unit: "#" },
      { path: "drivers.partnerUsers", label: "Paying users per partner", unit: "#" },
      { path: "drivers.partnerShare", label: "Partner revenue share", unit: "%" },
    ],
  },
  {
    title: "Cash and funding",
    fields: [
      { path: "drivers.funding", label: "New money in", unit: "$", step: 1000 },
      { path: "drivers.fundingMonth", label: "Month it arrives", unit: "month" },
      { path: "drivers.floor", label: "Cash floor to stay above", unit: "$", step: 1000 },
      { path: "drivers.servingPerUser", label: "AI and hosting per user a month", unit: "$", step: 0.01 },
    ],
  },
];

const getP = (o: any, p: string) => p.split(".").reduce((a, k) => (a == null ? a : a[k]), o);
const setP = (o: any, p: string, v: unknown) => {
  const ks = p.split(".");
  let a = o;
  for (let i = 0; i < ks.length - 1; i++) a = a[ks[i]!];
  a[ks[ks.length - 1]!] = v;
};
const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));
const monthLabel = (k: string) => new Date(k + "-15T12:00:00Z").toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });

function run(model: Model, P: any) {
  const [y, m] = P.as_of.split("-").map(Number);
  const now = new Date(y, m - 1, Number(P.as_of.slice(8)), 12);
  const sim = simulate(model, P.facts, { now, startCash: P.start_cash ?? 0, ctx: { runsPaid: P.pay_ctx.runsPaid, paid: new Set(P.pay_ctx.paid) } });
  const st = stats(sim, model);
  const months = sim.months.slice(1, st.H + 1);
  return {
    months,
    summary: {
      lowest_cash: st.lowest,
      new_money_needed: st.need,
      break_even: sim.breakEven,
      cash_out: sim.cashOut ? sim.cashOut.toISOString().slice(0, 10) : null,
      paying_users_end: Math.round(st.payingEnd),
      bairesdev_paid: st.bdPaid,
      bairesdev_left: st.bdLeft,
      through: months[months.length - 1]?.key,
    },
  };
}

// What's different from the starting scenario, in plain words.
function changes(base: any, cur: any): string[] {
  const out: string[] = [];
  for (const g of GROUPS) for (const f of g.fields) if (JSON.stringify(getP(base, f.path)) !== JSON.stringify(getP(cur, f.path))) out.push(`${f.label}: ${getP(base, f.path) ?? "none"} → ${getP(cur, f.path) ?? "none"}`);
  cur.ads.forEach((a: any, i: number) => {
    const b = base.ads[i];
    if (b && (b.monthly !== a.monthly || b.cac !== a.cac)) out.push(`${a.name}: $${b.monthly}/mo at $${b.cac} → $${a.monthly}/mo at $${a.cac}`);
  });
  cur.costs.forEach((c: any, i: number) => {
    const b = base.costs[i];
    if (b && (b.amount !== c.amount || (b.on !== false) !== (c.on !== false))) out.push(`${c.name}: ${b.on === false ? "off" : `$${b.amount}`} → ${c.on === false ? "off" : `$${c.amount}`}`);
  });
  if (!!base.drivers.raises !== !!cur.drivers.raises) out.push(`Owner raises: ${cur.drivers.raises ? "on" : "off"}`);
  if (base.drivers.pbMode !== cur.drivers.pbMode) out.push(`BairesDev payback: ${base.drivers.pbMode} → ${cur.drivers.pbMode}`);
  return out;
}

export function Planner() {
  const { toast } = useMe();
  const { data: P, error, reload } = useLoad<any>("/api/finance/planner");
  const [start, setStart] = useState<{ key: string; savedId?: string } | null>(null);
  const [model, setModel] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const startModel = useMemo(() => {
    if (!P || !start) return null;
    if (start.savedId) return P.saved.find((s: any) => s.id === start.savedId)?.model;
    return P.scenarios.find((s: any) => s.key === start.key)?.model;
  }, [P, start]);
  const baseModel = useMemo(() => (P && start ? P.scenarios.find((s: any) => s.key === start.key)?.model : null), [P, start]);

  if (!P) return <Loading error={error} />;
  if (!start) {
    const first = P.scenarios.find((s: any) => s.key === "base") ?? P.scenarios[0];
    setStart({ key: first.key });
    setModel(clone(first.model));
    return null;
  }
  const current = run(model, P);
  const original = run(startModel, P);
  const diff = changes(baseModel, model);
  const edited = JSON.stringify(model) !== JSON.stringify(startModel);
  const set = (path: string, v: unknown) => setModel((m: any) => { const n = clone(m); setP(n, path, v); return n; });

  const kpi = (label: string, v: string, was: string, worse?: boolean) => (
    <div className="stat">
      <span className="label">{label}</span>
      <span className="value" style={{ fontSize: 22 }}>{v}</span>
      {edited && v !== was && <span className="src" style={{ color: worse ? "var(--bad)" : "var(--good)" }}>was {was}</span>}
    </div>
  );
  const s = current.summary, o = original.summary;

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row between">
        <div className="row">
          <span className="muted small">Start from</span>
          <select
            className="input"
            style={{ width: "auto" }}
            value={start.savedId ? `saved:${start.savedId}` : start.key}
            onChange={(e) => {
              const v = e.target.value;
              if (v.startsWith("saved:")) {
                const sv = P.saved.find((x: any) => x.id === v.slice(6));
                setStart({ key: sv.base_key, savedId: sv.id });
                setModel(clone(sv.model));
              } else {
                setStart({ key: v });
                setModel(clone(P.scenarios.find((x: any) => x.key === v).model));
              }
            }}
          >
            <optgroup label="From the Budget Model">{P.scenarios.map((x: any) => <option key={x.key} value={x.key}>{x.name}</option>)}</optgroup>
            {P.saved.length > 0 && <optgroup label="Saved">{P.saved.map((x: any) => <option key={x.id} value={`saved:${x.id}`}>{x.bookmarked ? "★ " : ""}{x.name}</option>)}</optgroup>}
          </select>
          {edited && <button className="btn sm ghost" onClick={() => setModel(clone(startModel))}>Undo my changes</button>}
        </div>
        {P.can_save && <button className="btn primary" onClick={() => setSaving(true)}>Save as scenario</button>}
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        Uses the same formulas as your Budget Model (matched to the cent). Starting cash {usd(P.start_cash)} as of {P.as_of}. Every number here is a planning assumption, not a result.
      </p>

      <div className="grid-tight">
        {kpi("Lowest cash", usd(s.lowest_cash), usd(o.lowest_cash), s.lowest_cash < o.lowest_cash)}
        {kpi("Runs out of cash", s.cash_out ?? "Not in the plan", o.cash_out ?? "Not in the plan", !!s.cash_out && (!o.cash_out || s.cash_out < o.cash_out))}
        {kpi("Operating break-even", s.break_even ? monthLabel(s.break_even) : "Not in the plan", o.break_even ? monthLabel(o.break_even) : "Not in the plan", !s.break_even || (!!o.break_even && s.break_even > o.break_even))}
        {kpi("New money needed", usd(s.new_money_needed), usd(o.new_money_needed), s.new_money_needed > o.new_money_needed)}
        {kpi(`Paying users by ${s.through ? monthLabel(s.through) : "end"}`, s.paying_users_end.toLocaleString(), o.paying_users_end.toLocaleString(), s.paying_users_end < o.paying_users_end)}
      </div>

      <CashChart now={current.months} was={edited ? original.months : null} />

      {edited && diff.length > 0 && (
        <section className="card">
          <h2>What you changed</h2>
          <ul className="small" style={{ margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 4 }}>{diff.map((d) => <li key={d}>{d}</li>)}</ul>
        </section>
      )}

      <div className="grid">
        {GROUPS.map((g) => (
          <section key={g.title} className="card stack">
            <h2>{g.title}</h2>
            {g.fields.map((f) => <Input key={f.path} f={f} value={getP(model, f.path)} onChange={(v) => set(f.path, v)} />)}
            {g.title === "Cash and funding" && (
              <>
                <label className="check"><input type="checkbox" checked={!!model.drivers.raises} onChange={(e) => set("drivers.raises", e.target.checked)} /> Owner raises (Kayla and Megan move to proposed pay)</label>
                <label className="field">BairesDev payback
                  <select className="input" value={model.drivers.pbMode} onChange={(e) => set("drivers.pbMode", e.target.value)}>
                    <option value="surplus">Share of monthly surplus</option>
                    <option value="revenue">Share of revenue</option>
                    <option value="fixed">Fixed amount a month</option>
                    <option value="written">The written Aug 21 plan</option>
                  </select>
                </label>
                {model.drivers.pbMode === "surplus" && <Input f={{ path: "drivers.pbSurplusShare", label: "Share of surplus paid", unit: "%" }} value={model.drivers.pbSurplusShare} onChange={(v) => set("drivers.pbSurplusShare", v)} />}
                {model.drivers.pbMode === "fixed" && <Input f={{ path: "drivers.pbFixed", label: "Fixed payment a month", unit: "$" }} value={model.drivers.pbFixed} onChange={(v) => set("drivers.pbFixed", v)} />}
                {model.drivers.pbMode === "revenue" && <Input f={{ path: "drivers.pbPctRevenue", label: "Share of revenue paid", unit: "%" }} value={model.drivers.pbPctRevenue} onChange={(v) => set("drivers.pbPctRevenue", v)} />}
              </>
            )}
          </section>
        ))}
        <section className="card stack">
          <h2>Ads</h2>
          {model.ads.map((a: any, i: number) => (
            <div key={a.id} className="form-2">
              <Input f={{ path: "", label: `${a.name}: monthly spend`, unit: "$", step: 100 }} value={a.monthly} onChange={(v) => set(`ads.${i}.monthly`, v)} />
              <Input f={{ path: "", label: "Cost per paying user", unit: "$" }} value={a.cac} onChange={(v) => set(`ads.${i}.cac`, v)} />
            </div>
          ))}
          <p className="muted tiny" style={{ margin: 0 }}>Month-by-month overrides from the Budget Model (like a bigger December) are kept as they were.</p>
        </section>
        <section className="card stack">
          <h2>People and costs</h2>
          {model.costs.map((c: any, i: number) => (
            <div key={c.id} className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={c.on !== false} onChange={(e) => set(`costs.${i}.on`, e.target.checked)} aria-label={`Include ${c.name}`} />
              <span className="small" style={{ flex: 1, opacity: c.on === false ? 0.5 : 1 }}>{c.name}{c.kind === "once" ? ` (once, ${c.month})` : ""}</span>
              <input className="input" style={{ width: 110 }} type="number" value={c.amount} onChange={(e) => set(`costs.${i}.amount`, Number(e.target.value))} />
            </div>
          ))}
        </section>
      </div>

      <section className="card" style={{ padding: 0 }}>
        <div className="scroll-x">
          <table className="t" style={{ fontVariantNumeric: "tabular-nums" }}>
            <thead><tr><th>Month</th><th style={{ textAlign: "right" }}>Paying users</th><th style={{ textAlign: "right" }}>Cash in</th><th style={{ textAlign: "right" }}>Costs</th><th style={{ textAlign: "right" }}>BairesDev</th><th style={{ textAlign: "right" }}>Month-end cash</th></tr></thead>
            <tbody>
              {current.months.map((m: any) => (
                <tr key={m.key}>
                  <td>{monthLabel(m.key)}</td>
                  <td style={{ textAlign: "right" }}>{m.u ? Math.round(m.u.paying).toLocaleString() : "—"}</td>
                  <td style={{ textAlign: "right" }}>{usd(m.subsCash + m.merch + m.funding)}</td>
                  <td style={{ textAlign: "right" }}>{usd(m.opex)}</td>
                  <td style={{ textAlign: "right" }}>{m.pay ? usd(m.pay) : "—"}</td>
                  <td style={{ textAlign: "right", color: m.end < 0 ? "var(--bad)" : undefined }}>{usd(m.end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <SavedList P={P} onOpen={(sv) => { setStart({ key: sv.base_key, savedId: sv.id }); setModel(clone(sv.model)); window.scrollTo({ top: 0, behavior: "smooth" }); }} onChange={reload} />

      {saving && (
        <Drawer title="Save this scenario" onClose={() => setSaving(false)}>
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              try {
                await api("/api/finance/saved", { method: "POST", body: { name: fd.get("name"), note: fd.get("note"), bookmarked: fd.get("bookmarked") === "on", base_key: start.key, model, summary: { ...current.summary, changes: diff } } });
                toast("Scenario saved");
                setSaving(false);
                reload();
              } catch (err: any) {
                toast(err.message, true);
              }
            }}
          >
            <label className="field">Name<input className="input" name="name" required placeholder="e.g. Pause Meta, double Apple Search Ads" autoFocus /></label>
            <label className="field">Note to future you<textarea className="input" name="note" placeholder="Why you looked at this, and what you'd need to believe" /></label>
            <label className="check"><input type="checkbox" name="bookmarked" defaultChecked /> Bookmark it</label>
            {diff.length > 0 && <div className="small muted">Saves your {diff.length} change{diff.length === 1 ? "" : "s"} from the {P.scenarios.find((x: any) => x.key === start.key)?.name} scenario, and today's results.</div>}
            <div className="row"><button className="btn primary">Save</button></div>
          </form>
        </Drawer>
      )}
    </div>
  );
}

function Input({ f, value, onChange }: { f: Field; value: any; onChange: (v: any) => void }) {
  return (
    <label className="field">
      {f.label}
      <span className="box" style={{ display: "flex", alignItems: "center", border: "1px solid var(--line-strong)", borderRadius: 10, padding: "0 10px", background: "var(--panel)" }}>
        {f.unit === "$" && <span className="muted small">$</span>}
        <input
          className="input"
          style={{ border: "none", padding: "8px 4px", background: "transparent" }}
          type={f.unit === "month" ? "month" : "number"}
          step={f.step ?? 1}
          value={value ?? ""}
          onChange={(e) => onChange(f.unit === "month" ? e.target.value : e.target.value === "" ? 0 : Number(e.target.value))}
        />
        {f.unit === "%" && <span className="muted small">%</span>}
      </span>
      {f.hint && <span className="muted tiny" style={{ fontWeight: 400 }}>{f.hint}</span>}
    </label>
  );
}

function CashChart({ now, was }: { now: any[]; was: any[] | null }) {
  const W = 760, H = 200, pad = 36;
  const all = [...now.map((m) => m.end), ...(was ?? []).map((m) => m.end), 0];
  const lo = Math.min(...all), hi = Math.max(...all);
  const x = (i: number) => pad + (i * (W - pad * 2)) / Math.max(1, now.length - 1);
  const y = (v: number) => H - pad / 2 - ((v - lo) / Math.max(1, hi - lo)) * (H - pad);
  const line = (ms: any[]) => ms.map((m, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(m.end).toFixed(1)}`).join(" ");
  return (
    <section className="card">
      <div className="row between"><h2>Month-end cash</h2><span className="small muted">{was ? "Pink is your version; grey is where you started." : ""}</span></div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", marginTop: 8 }} role="img" aria-label="Month-end cash over time">
        <line x1={pad} x2={W - pad} y1={y(0)} y2={y(0)} stroke="var(--line-strong)" strokeDasharray="4 4" />
        <text x={4} y={y(0) + 4} fontSize="11" fill="var(--muted)">$0</text>
        {was && <path d={line(was)} fill="none" stroke="var(--muted)" strokeWidth="2" opacity="0.6" />}
        <path d={line(now)} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
        {now.map((m, i) => (i % 3 === 0 ? <text key={m.key} x={x(i)} y={H - 2} fontSize="11" textAnchor="middle" fill="var(--muted)">{monthLabel(m.key)}</text> : null))}
      </svg>
    </section>
  );
}

function SavedList({ P, onOpen, onChange }: { P: any; onOpen: (s: any) => void; onChange: () => void }) {
  const { toast } = useMe();
  if (!P.saved.length) return <Empty>No saved scenarios yet. Change anything above, then Save as scenario.</Empty>;
  return (
    <section className="card stack">
      <h2>Saved scenarios</h2>
      <div className="list">
        {P.saved.map((s: any) => (
          <div key={s.id} className="item" style={{ alignItems: "flex-start" }}>
            <button className="btn ghost sm" style={{ padding: "0 4px", fontSize: 18, color: s.bookmarked ? "var(--accent)" : "var(--muted)" }} title={s.bookmarked ? "Remove bookmark" : "Bookmark"} onClick={async () => { await api(`/api/finance/saved/${s.id}`, { method: "PATCH", body: { bookmarked: !s.bookmarked } }); onChange(); }}>
              {s.bookmarked ? "★" : "☆"}
            </button>
            <div className="grow">
              <div className="title">{s.name}</div>
              <div className="small muted">
                From {P.scenarios.find((x: any) => x.key === s.base_key)?.name ?? s.base_key} · {s.created_by_name} · {ago(s.created_at)}
                {s.import_id !== P.import_id && <> · <Pill tone="warn">Saved on an older import</Pill></>}
              </div>
              {s.note && <div className="small" style={{ marginTop: 4 }}>{s.note}</div>}
              <div className="small muted" style={{ marginTop: 4 }}>
                When saved: lowest cash {usd(s.summary.lowest_cash)}, break-even {s.summary.break_even ?? "not in plan"}, {Number(s.summary.paying_users_end ?? 0).toLocaleString()} paying users by {s.summary.through ?? "end"}
              </div>
            </div>
            <div className="row">
              <button className="btn sm" onClick={() => onOpen(s)}>Open</button>
              <button className="btn sm ghost danger" onClick={async () => { if (!confirm(`Delete "${s.name}"?`)) return; await api(`/api/finance/saved/${s.id}`, { method: "DELETE" }).catch((e) => toast(e.message, true)); onChange(); }}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
